import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    IZCoreLocation, ZCoreLocation, IEntityFormValidations, EntityFormValidations,
    IEntityValidations, EntityValidations, ISpecificEntityFormProps
} from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { OrbUserContext } from '../OrbUserContext';
import axios from 'axios';

class ZCoreLocationValidations extends EntityValidations {
    public Address1: string = null;
}

export const ZCoreLocationForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { orbConfig } = useContext(OrbUserContext);
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { entityStatuses } } = useContext(DataContext);

    useMemo(() => entityStatuses(), [entityStatuses]);

    const [countries, setCountries] = useState<string[]>([]);
    const [countriesLoading, setCountriesLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setCountriesLoading(true);
                const zedxNetapiURL = process.env.REACT_APP_ZedX_Net_API!;
                const customerCountriesApi = `${zedxNetapiURL}/CustomerCountries/GetAll`;

                const result = (await axios.get(customerCountriesApi)).data;

                setCountries(result.map((c: { Title: string }) => c.Title));
            } catch (error) {
                console.error('Error loading countries:', error);
            } finally {
                setCountriesLoading(false);
            }
        };

        fetchCountries();
    }, []);

    const validateEntity = (zCoreLocation: IZCoreLocation): Promise<ZCoreLocationValidations> => {
        const errors = new ZCoreLocationValidations();

        if (zCoreLocation.Title === null || zCoreLocation.Title === '') {
            errors.Title = 'Location name is required';
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (zCoreLocation.Address1 === null || zCoreLocation.Address1 === '') {
            errors.Address1 = 'Address is required';
            errors.Valid = false;
        }
        else {
            errors.Address1 = null;
        }

        return Promise.resolve(errors);
    };

    return (
        <EntityForm<IZCoreLocation, ZCoreLocationValidations>
            {...props}
            entityName="Location"
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: zCoreLocation, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Title"
                            maxLength={300}
                            className={styles.formField}
                            required={true}
                            value={zCoreLocation.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title} />
                        <CrTextField
                            label="Address 1"
                            maxLength={300}
                            className={styles.formField}
                            required={true}
                            value={zCoreLocation.Address1}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Address1')}
                            errorMessage={errors.Address1} />
                        <CrTextField
                            label="Address 2"
                            maxLength={300}
                            className={styles.formField}
                            value={zCoreLocation.Address2}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Address2')} />
                        <CrTextField
                            label="City"
                            maxLength={150}
                            className={styles.formField}
                            value={zCoreLocation.City}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'City')} />
                        <CrTextField
                            label="State/Region"
                            maxLength={150}
                            className={styles.formField}
                            value={zCoreLocation.StateRegion}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'StateRegion')} />
                        <CrTextField
                            label="Zip/Postcode"
                            maxLength={30}
                            className={styles.formField}
                            value={zCoreLocation.ZipPostcode}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'ZipPostcode')} />
                        <CrDropdown
                            label="Country"
                            className={styles.formField}
                            disabled={countriesLoading}
                            placeholder={countriesLoading ? 'Loading countries...' : undefined}
                            options={LookupService.entitiesToSelectableOptionsCustom(
                                countries.map(c => ({ ID: c, Title: c }))
                            )}
                            selectedKey={zCoreLocation.Country}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'Country')} />
                        <CrTextField
                            label="Switchboard"
                            maxLength={50}
                            className={styles.formField}
                            value={zCoreLocation.Switchboard}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Switchboard')} />
                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={zCoreLocation.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')} />
                    </div>
                );
            }}
            loadEntity={id => dataServices.zCoreLocationService.read(id, true, true)}
            loadNewEntity={() => new ZCoreLocation()}
            loadEntityValidations={() => new ZCoreLocationValidations()}
            onValidateEntity={validateEntity}
            onCreate={g => dataServices.zCoreLocationService.create(g)}
            onUpdate={g => dataServices.zCoreLocationService.update(g.ID, g)}
        />
    );
};